import React, { useEffect, useState } from 'react';
import {
    CreditCardTypeEnum,
    TokenType,
    TokensVService as TokensV2Service
} from '../../services/V2/gozer';
import { TokensVService } from '../../services/V3/gozer';
import { WalletAdd } from './WalletAdd';
import { canAcceptAnything, dateToLocal, handleError } from '../Shared/Functions';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Paper } from '@mui/material';
import { withToast } from '../Shared/Toast';
import { CustomTable } from '../Shared/CustomTable';
import StarIcon from '@mui/icons-material/Star';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LoopIcon from '@mui/icons-material/Loop';
import CCImage from './CC/CCImage';
import { ConfirmModal } from '../Shared/ConfirmModal';
import { WalletUpdate } from './WalletUpdate';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DOMPurify from 'dompurify';
import { checkExpirationDate, canAcceptAch, canAcceptCC } from '../Shared/Functions';
import ThirdPartyACH from './ACH/ThirdPartyACH';

const Wallet = ({ account, successToast, errorToast }) => {
    const [loading, setLoading] = useState<boolean>(true)
    const [addModal, setAddModal] = useState<boolean>(false)
    const [noMethodsModal, setNoMethodsModal] = useState<boolean>(false)
    const [tokens, setTokens] = useState<any[]>([])
    const [showAutopay, setShowAutopay] = useState<string | null>(null);
    const [showDisableAutopay, setShowDisableAutopay] = useState<string | null>(null);
    const [showDelete, setShowDelete] = useState<string | null>(null);
    const [showUpdate, setShowUpdate] = useState<string | null>(null);
    const [showAddPrimary, setShowAddPrimary] = useState<string | null>(null);
    const [showRemovePrimary, setShowRemovePrimary] = useState<string | null>(null);
    const [isPrimary, setIsPrimary] = useState<boolean>(false);
    const [isRecurring, setIsRecurring] = useState<boolean>(false);
    const [paymentmethod, setPaymentmethod] = useState<any>(null);
    const [ePaymentNotAllowedModal, setEPaymentNotAllowedModal] = useState<boolean>(false);
    const [acceptCC, setAcceptCC] = useState<boolean>()
    const [acceptACH, setAcceptACH] = useState<boolean>()
    const [methodMode, setMethodMode] = useState<string | null>(null)
    const [isThirdPartyAchRecurring, setIsThirdPartyAchRecurring] = useState<boolean>(false)
    const [showThirdPartyCreate, setShowThirdPartyCreate] = useState<boolean>(false)

    useEffect(() => {
        if (account) {
            getTokens()
            setAcceptACH(canAcceptAch(account.paymentInfo))
            setAcceptCC(canAcceptCC(account.paymentInfo))
            setIsThirdPartyAchRecurring(!(account.paymentInfo?.ach?.isI3Recurring ?? true))
        }
    }, [account])// eslint-disable-line react-hooks/exhaustive-deps

    const getTokens = () => {
        setMethodMode(null)
        setLoading(true)
        TokensV2Service.tokensVGetAll(account.id)
            .then(x => {
                setTokens(x.filter(y => account?.paymentInfo?.ePaymentAllowed && (
                    (y.tokenType === TokenType.ACH && !account.disallowACHPayments && account.paymentInfo.ach.allowPayment) ||
                    (
                        y.tokenType === TokenType.CC && !account.disallowCCPayments && account.paymentInfo.cc.allowPayment && (
                            (y.cardType === CreditCardTypeEnum.AMERICAN_EXPRESS && account.paymentInfo.cc.acceptAmex) ||
                            (y.cardType === CreditCardTypeEnum.DISCOVER && account.paymentInfo.cc.acceptDiscover) ||
                            (y.cardType === CreditCardTypeEnum.MASTER_CARD && account.paymentInfo.cc.acceptMasterCard) ||
                            (y.cardType === CreditCardTypeEnum.VISA && account.paymentInfo.cc.acceptVisa)
                        )
                    ))
                ))
                setLoading(false)
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                setLoading(false)
            })
    }

    const showAddModal = () => {
        if (canAcceptAnything(account.paymentInfo)) {
            setAddModal(true)
        } else if (!account.paymentInfo.ePaymentAllowed) {
            setEPaymentNotAllowedModal(true)
        } else {
            setNoMethodsModal(true);
        }
    }

    const addAutopay = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdateRecurring(account.id, showAutopay ?? "", enable)
            .then(result => {
                successToast("AutoPay Updated")
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                getTokens()
            })
        setShowAutopay(null)
    }

    const removeAutopay = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdateRecurring(account.id, showDisableAutopay ?? "", enable)
            .then(result => {
                successToast("AutoPay Updated")
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                getTokens()
            })
        setShowDisableAutopay(null)
    }

    const selectPaymentMethod = (item) => {
        setPaymentmethod(item)
    }

    const deleteItem = () => {
        setLoading(true)
        TokensVService
            .tokensVDeleteToken(account.id, showDelete ?? "")
            .then(result => {
                const successMessage = paymentmethod.tokenType === TokenType.CC
                    ? "Credit Card Deleted"
                    : "Bank Account Deleted";
                successToast(successMessage)
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                getTokens()
            })
        setShowDelete(null)
    }

    const addPrimary = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdatePrimary(account.id, showAddPrimary ?? "", enable)
            .then(result => {
                successToast("Primary Method Updated")
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                getTokens()
            })
        setShowAddPrimary(null)
    }

    const removePrimary = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdatePrimary(account.id, showRemovePrimary ?? "", enable)
            .then(result => {
                successToast("Primary Method Updated")
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleError(err))
                getTokens()
            })
        setShowRemovePrimary(null)
    }

    const sanitizedTerms = () => ({
        __html: DOMPurify.sanitize(account.termsAndConditions?.recurringPaymentTermsAndConditions),
    });

    return loading ?
        <div className='loading-container'><CircularProgress aria-label="Loading wallet data" className='center-spinner' color="primary" /></div> :
        <>
            {/* confirm modals */}
            <ConfirmModal
                fullWidth={false}
                cancelCallback={() => setShowRemovePrimary(null)}
                show={showRemovePrimary !== null}
                confirmCallback={() => removePrimary(false)}
                title="Remove As Primary"
                description="Are you sure you want to remove this as your primary payment method?" />
            <ConfirmModal
                fullWidth={false}
                cancelCallback={() => setShowAddPrimary(null)}
                show={showAddPrimary !== null}
                confirmCallback={() => addPrimary(true)}
                title="Add As Primary"
                description="Are you sure you want to make this your primary payment method?" />
            <ConfirmModal
                fullWidth={true}
                cancelCallback={() => setShowAutopay(null)}
                show={showAutopay !== null}
                confirmCallback={() => addAutopay(true)}
                title={"Enable AutoPay"}
                description={account.termsAndConditions?.recurringPaymentTermsAndConditions ?
                    <div dangerouslySetInnerHTML={sanitizedTerms()} style={{ fontSize: "16px" }} /> :
                    "Are you sure that you would like to enable this payment method for AutoPay?"} />
            <ConfirmModal
                fullWidth={false}
                cancelCallback={() => setShowDisableAutopay(null)}
                show={showDisableAutopay !== null}
                confirmCallback={() => removeAutopay(false)}
                title={"Disable AutoPay"}
                description={tokens.find(x => x.id === showDisableAutopay)?.isThirdParty ?
                    `By submitting this form I understand that I am cancelling my automatic bank draft authorization.
                    This cancellation will take effect once processed, which may take up to 1 business day.
                    I acknowledge that payments already scheduled may still be withdrawn, and that I remain responsible for paying any outstanding balance by the due date.
                    If you have any questions or need help with your billing, feel free to contact ${account.districtInfo.name} - we are always here to help.` :
                    "Are you sure that you would like to disable AutoPay for this payment method?"} />
            <ConfirmModal
                fullWidth={false}
                cancelCallback={() => setShowDelete(null)}
                show={showDelete !== null}
                confirmCallback={deleteItem}
                title={"Delete Payment Method"}
                description={tokens.find(x => x.id === showDelete)?.isThirdParty ?
                    (`By submitting this form I understand that I am cancelling my automatic bank draft authorization.
                    This cancellation will take effect once processed, which may take up to 1 business day.
                    I acknowledge that payments already scheduled may still be withdrawn, and that I remain responsible for paying any outstanding balance by the due date.
                    If you have any questions or need help with your billing, feel free to contact ${account.districtInfo.name} - we are always here to help.`) :
                    (<><div>Are you sure that you would like to delete this payment method?</div>
                        {isPrimary ? <div>This payment method is currently set as your primary payment method.</div> : ""}
                        {isRecurring ? <div>This payment method is currently enrolled in autopay.</div> : ""} </>)} />
            {/* confirm modals end */}
            {/* update modal */}
            {showUpdate &&
                <WalletUpdate
                    success={successToast}
                    fail={errorToast}
                    paymentMethod={showUpdate ?? ""}
                    account={account}
                    itemAdded={() => {
                        setShowUpdate(null)
                        getTokens()
                    }}
                    show={showUpdate !== null}
                    hide={() => setShowUpdate(null)} />
            }
            {/* update modal end */}
            <Dialog onClose={() => { }} open={noMethodsModal}>
                <DialogTitle>Account Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        We’re sorry, online payments are not allowed for this account.
                        Please contact your utility office
                    </p>
                </DialogContent>
                <DialogActions className='restricted-modal-buttons-group'>
                    <Button variant="contained" onClick={() => setNoMethodsModal(false)}>Ok</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                onClose={() => { }}
                open={ePaymentNotAllowedModal}>
                <DialogTitle>Payment Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        This utility provider is unable to accept payments.
                        Please contact Customer Service. We apologize for any inconvenience.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => setEPaymentNotAllowedModal(false)}
                            className="m-2">Ok</Button>
                    </div>
                </DialogContent>
            </Dialog>
            {addModal &&
                <WalletAdd
                    success={successToast}
                    fail={errorToast}
                    account={account}
                    methodMode={methodMode}
                    itemAdded={() => {
                        setAddModal(false)
                        getTokens()
                    }}
                    tokenCreateError={() => {
                        setAddModal(false)
                    }} />
            }
            {showThirdPartyCreate &&
                <ThirdPartyACH
                    success={() => {
                        successToast("Successfully added bank draft.")
                        getTokens()
                        setShowThirdPartyCreate(false)
                    }}
                    fail={errorToast}
                    account={account}
                    close={() => setShowThirdPartyCreate(false)}
                />
            }
            <Grid container>
                <Grid item xs={12} >
                    <Paper>
                        <Grid container xs={12} style={{ padding: "20px 20px 50px 20px" }}>
                            <Grid item className='clickable' style={{ paddingBottom: "5px" }} xs={12} role="presentation">
                                <div><b>Add New Method</b></div>
                            </Grid>
                            <Grid item className='clickable' xs={12} role="presentation">
                                <nav aria-label="Payment method options">
                                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", gap: "1em" }}>
                                        {(!account.disallowCCPayments && acceptCC) &&
                                            <li>
                                                <Link onClick={() => { setMethodMode("CC"); showAddModal() }} style={{ paddingRight: "10px" }}>Credit Card</Link>
                                            </li>
                                        }
                                        {(isThirdPartyAchRecurring || (!account.disallowACHPayments && acceptACH)) &&
                                            <li>
                                                <Link onClick={() => {
                                                    setMethodMode("ACH");
                                                    if (isThirdPartyAchRecurring)
                                                        setShowThirdPartyCreate(true)
                                                    else
                                                        showAddModal()
                                                }}>
                                                    Bank Account
                                                </Link>
                                            </li>
                                        }
                                    </ul>
                                </nav>
                            </Grid>
                            <CustomTable
                                title={''}
                                refresh={loading}
                                setRefresh={setLoading}
                                columns={[
                                    {
                                        field: "isPrimary",
                                        title: "Primary",
                                        editable: 'never',
                                        render: params =>
                                            <div>
                                                {((params.tokenType === TokenType.ACH) || (params.tokenType === TokenType.CC &&
                                                    checkExpirationDate(params.expirationMonth, params.expirationYear) !== "Expired")) &&
                                                    <div className="clickable" onClick={() => { !params.isPrimary ? setShowAddPrimary(params.id) : setShowRemovePrimary(params.id) }} style={{ color: params.isPrimary ? "#0073A8" : "#595959" }} role="button" tabIndex={0} aria-label={params.isPrimary ? "Remove as primary payment method" : "Set as primary payment method"} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !params.isPrimary ? setShowAddPrimary(params.id) : setShowRemovePrimary(params.id); } }}>
                                                        <StarIcon style={{ fontSize: "25px" }} />
                                                    </div>
                                                }
                                            </div>,
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "tokenType",
                                        title: "Payment Method",
                                        editable: 'never',
                                        render: params =>
                                            <div>
                                                {params.tokenType === TokenType.ACH ?
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ marginRight: "5px" }}>
                                                                <AccountBalanceIcon />
                                                            </div>
                                                            <div style={{ marginLeft: "5px" }}>
                                                                ****{params.last4}
                                                            </div>
                                                        </div>
                                                        {params.isThirdParty &&
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <div style={{ marginRight: "10px" }}>{params.bankName}</div>
                                                            </div>
                                                        }
                                                    </div>
                                                    :
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ marginRight: "5px" }}>
                                                                <CCImage token={params} className="cc-vendor-image-sm" />
                                                            </div>
                                                            <div style={{ marginLeft: "5px" }}>
                                                                ****{params.last4}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ marginRight: "10px" }}>{params.expirationMonth ?? "N"}/{params.expirationYear ?? "A"}</div>
                                                            {checkExpirationDate(params.expirationMonth, params.expirationYear) === "Expired" ?
                                                                <div style={{ color: "#b71c1c", marginLeft: "10px" }}>Expired</div>
                                                                : checkExpirationDate(params.expirationMonth, params.expirationYear) === "Expiring" ?
                                                                    <div style={{ color: "#b71c1c", marginLeft: "10px" }}>Expiring</div>
                                                                    : <></>
                                                            }
                                                        </div>
                                                    </div>
                                                }
                                            </div>,
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "isRecurring",
                                        title: "Manage Autopay",
                                        tooltip: isThirdPartyAchRecurring ?
                                            "Make payments easy. Use a credit card for AutoPay, or select Add a Bank Account to set up Automated Bank Draft and skip the fee." :
                                            "Make payments easy. Use a credit card or bank account for AutoPay by adding a new method or choosing one you’ve already saved.",
                                        editable: 'never',
                                        render: params =>
                                        (<div>
                                            {params.tokenType === TokenType.ACH && isThirdPartyAchRecurring && !params.isThirdParty ? "" :
                                                ((params.tokenType === TokenType.ACH && account.paymentInfo.ach.allowRecurringPayment) ||
                                                    (params.tokenType === TokenType.CC && account.paymentInfo.cc.allowRecurringPayment &&
                                                        checkExpirationDate(params.expirationMonth, params.expirationYear) !== "Expired")) &&
                                                    <div className="clickable"
                                                        onClick={() => { !params.isRecurring ? setShowAutopay(params.id) : setShowDisableAutopay(params.id) }}
                                                        style={{ color: params.isRecurring ? "#2e7d32" : "#595959", alignItems: "center", display: "flex", flexDirection: "column" }}
                                                        role="button" tabIndex={0} aria-label={params.isRecurring ? "Unenroll from autopay" : "Enroll in autopay"}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault(); !params.isRecurring ? setShowAutopay(params.id) : setShowDisableAutopay(params.id);
                                                            }
                                                        }}>
                                                        <LoopIcon style={{ fontSize: "25px" }} />
                                                        <div>
                                                            {!params.isRecurring ?
                                                                <div>Enroll Autopay</div> :
                                                                <div>Unenroll Autopay</div>}
                                                        </div>
                                                    </div>
                                            }
                                        </div>),
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "enrolledDate",
                                        title: "",
                                        editable: 'never',
                                        render: params =>
                                        (<div>
                                            {params.isRecurring && params.isThirdParty &&
                                                <div>
                                                    Enrolled: {dateToLocal(params.createdAt).toDateString()}
                                                </div>
                                            }
                                        </div>),
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "",
                                        title: "Actions",
                                        editable: 'never',
                                        render: params =>
                                            params.isThirdParty ?
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <div className="clickable" onClick={() => {
                                                        selectPaymentMethod(params)
                                                        setIsPrimary(params.isPrimary)
                                                        setIsRecurring(params.isRecurring)
                                                        setShowDelete(params.id);
                                                    }}
                                                        style={{ color: "black", width: "16%", marginRight: "10px" }}
                                                        role="button" tabIndex={0} aria-label="Delete payment method"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault(); selectPaymentMethod(params);
                                                                setIsPrimary(params.isPrimary);
                                                                setIsRecurring(params.isRecurring);
                                                                setShowDelete(params.id);
                                                            }
                                                        }}>
                                                        <DeleteIcon style={{ fontSize: "25px" }} />
                                                        <div>
                                                            Delete
                                                        </div>
                                                    </div>
                                                </div> :
                                                (<div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <div className="clickable" onClick={() => setShowUpdate(params)}
                                                        style={{ color: "black", width: "16%", marginRight: "30px" }}
                                                        role="button" tabIndex={0}
                                                        aria-label="Edit payment method"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setShowUpdate(params);
                                                            }
                                                        }}>
                                                        <EditIcon style={{ fontSize: "25px" }} />
                                                        <div>Edit</div>
                                                    </div>
                                                    <div className="clickable" onClick={() => {
                                                        selectPaymentMethod(params)
                                                        setIsPrimary(params.isPrimary)
                                                        setIsRecurring(params.isRecurring)
                                                        setShowDelete(params.id);
                                                    }} style={{ color: "black", width: "16%", marginRight: "10px" }}
                                                        role="button" tabIndex={0} aria-label="Delete payment method"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                selectPaymentMethod(params);
                                                                setIsPrimary(params.isPrimary);
                                                                setIsRecurring(params.isRecurring);
                                                                setShowDelete(params.id);
                                                            }
                                                        }}>
                                                        <DeleteIcon style={{ fontSize: "25px" }} />
                                                        <div>
                                                            Delete
                                                        </div>
                                                    </div>
                                                </div>),
                                        hidden: false,
                                        sorting: false
                                    }
                                ]}
                                dataEndpoint={async query => {
                                    let data = tokens
                                    setLoading(false)
                                    return {
                                        data: data ?? [],
                                        totalCount: data?.length ?? 0,
                                        numberPerPage: 10
                                    }
                                }}
                                actions={[]}
                                pageSize={10}
                                onRowClick={null}
                            />
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </>
}
export default withToast(Wallet);